import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X, Volume2, VolumeX } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import FallingPetals from '../../../components/FallingPetals';

/**
 * Counts from 0 to `target` over `duration` ms with ease-out cubic.
 * Inlined here (not imported) so it always runs inside the same React
 * instance — avoids "Cannot read properties of null (reading 'useState')"
 * which fires when a hook file is resolved through a different module copy.
 */
function useCountUp(target, duration = 1500, active = true) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    const end = Number(target) || 0;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || end === 0) { setValue(end); return undefined; }
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      setValue(Math.round(end * (1 - Math.pow(1 - t, 3))));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration, active]);
  return value;
}

/**
 * OkoiyaWrapped v2 — full-screen animated wedding recap.
 *
 * <OkoiyaWrapped data={reportData} onClose={() => ...} />
 *
 * `data` comes from get_memory_report_data (dashboard) or
 * get_wrapped_public_data (public /wrapped/:slug — sets public_mode,
 * carries no money and no guest messages; those slides auto-skip).
 *
 * No Framer Motion (project rule): CSS keyframes + rAF + touch handlers.
 * Per-couple theme: a hash of the wedding slug/id picks one of four
 * gradient palettes, so every couple's Wrapped looks like *theirs* —
 * stable for them, different from their friends'.
 */

const TERRACOTTA = '#C4826A';
const CURRENCY_SYMBOLS = { NGN: '\u20A6', USD: '$', GBP: '\u00A3', EUR: '\u20AC', GHS: '\u20B5', KES: 'KSh', ZAR: 'R' };

const APP_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_URL) ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://okoiya.com');

const THEMES = [
  { // Ember — the classic
    guests: 'linear-gradient(160deg, #C4826A 0%, #B05A45 100%)',
    journey: 'linear-gradient(160deg, #4A6B4A 0%, #2D4A2D 100%)',
    gifts: 'linear-gradient(160deg, #C4A24A 0%, #A07830 100%)',
    asoebi: 'linear-gradient(160deg, #8B3A2A 0%, #5C2018 100%)',
    accent: '#C4826A',
  },
  { // Wine & Gold
    guests: 'linear-gradient(160deg, #7A3B4A 0%, #4A1F2C 100%)',
    journey: 'linear-gradient(160deg, #5F7A5F 0%, #324832 100%)',
    gifts: 'linear-gradient(160deg, #B08C3E 0%, #7E5F22 100%)',
    asoebi: 'linear-gradient(160deg, #A5654E 0%, #6E3E2A 100%)',
    accent: '#D9A05B',
  },
  { // Dusk
    guests: 'linear-gradient(160deg, #4E5D78 0%, #2B3550 100%)',
    journey: 'linear-gradient(160deg, #6E8A6E 0%, #3E5440 100%)',
    gifts: 'linear-gradient(160deg, #C4826A 0%, #9A5A40 100%)',
    asoebi: 'linear-gradient(160deg, #70354A 0%, #441C2C 100%)',
    accent: '#C4826A',
  },
  { // Earth
    guests: 'linear-gradient(160deg, #9C6B4F 0%, #6B4030 100%)',
    journey: 'linear-gradient(160deg, #3E6B6B 0%, #1F4444 100%)',
    gifts: 'linear-gradient(160deg, #CA9A4E 0%, #8F6A28 100%)',
    asoebi: 'linear-gradient(160deg, #8B3A2A 0%, #4F1D12 100%)',
    accent: '#E0B089',
  },
];

export default function OkoiyaWrapped({ data, onClose }) {
  const publicMode = !!data?.public_mode;
  const w = data?.wedding || {};
  const guests = data?.guests || {};
  const photos = data?.photos || {};
  const tasks = data?.tasks || {};
  const gifts = data?.gifts || {};
  const registry = data?.registry || {};
  const asoebi = data?.asoebi || {};
  const messages = Array.isArray(data?.messages) ? data.messages : [];

  const symbol = CURRENCY_SYMBOLS[w.currency] || '\u20A6';
  const money = (n) => symbol + Number(n || 0).toLocaleString();

  const theme = useMemo(() => {
    const s = String(w.slug || w.id || 'okoiya');
    let h = 0;
    for (const c of s) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    return THEMES[h % THEMES.length];
  }, [w.slug, w.id]);

  // ── which slides exist (no-data slides skipped cleanly) ─────────
  const galleryUrls = useMemo(() => {
    const s = Array.isArray(photos.studio_urls) ? photos.studio_urls.slice(0, 3) : [];
    const g = Array.isArray(photos.guest_urls) ? photos.guest_urls.slice(0, 3) : [];
    return [...s, ...g].slice(0, 5);
  }, [photos]);

  const slides = useMemo(() => {
    const s = ['opening'];
    if ((guests.arrived || guests.total) > 0) s.push('guests');
    if ((w.days_planning || 0) > 0 || (tasks.completed || 0) > 0) s.push('journey');
    if ((photos.guest_total || 0) + (photos.studio_total || 0) > 0) s.push('photos');
    if (galleryUrls.length >= 3) s.push('gallery');
    if (!publicMode && messages.length > 0) s.push('messages');
    if (!publicMode && ((registry.received || 0) > 0 || (gifts.cash_total || 0) > 0)) s.push('gifts');
    if ((asoebi.orders || 0) > 0) s.push('asoebi');
    s.push('closing');
    return s;
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const [started, setStarted] = useState(false);
  const [index, setIndex] = useState(0);
  const [closing, setClosing] = useState(false);
  const [muted, setMuted] = useState(false);
  const dirRef = useRef(1);
  const touchX = useRef(null);
  const audioRef = useRef(null);
  const ytRef = useRef(null);

  // ── music: YouTube link from Our Story, else fallback mp3 ───────
  const ytId = w.music_youtube_url ? extractYouTubeId(w.music_youtube_url) : null;
  const fallbackAudioUrl = useMemo(
    () => supabase.storage.from('assets').getPublicUrl('audio/okoiya-wedding-instrumental.mp3').data.publicUrl,
    []
  );

  const begin = () => {
    setStarted(true);
    // play() inside the tap gesture — satisfies autoplay policies everywhere,
    // including visitors arriving cold on the public /wrapped/:slug link
    if (!ytId && audioRef.current) audioRef.current.play().catch(() => {});
  };

  const go = (delta) => {
    if (!started) return;
    dirRef.current = delta;
    setIndex((i) => Math.min(Math.max(i + delta, 0), slides.length - 1));
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 300);
  };

  // ── keyboard + scroll lock ──────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [slides.length, started]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (audioRef.current) audioRef.current.muted = next;
    if (ytRef.current?.contentWindow) {
      ytRef.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: next ? 'mute' : 'unMute', args: [] }),
        '*'
      );
    }
  };

  // ── swipe ───────────────────────────────────────────────────────
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx < -50) go(1);
    else if (dx > 50) go(-1);
    touchX.current = null;
  };

  const current = slides[index];

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100dvh',
        zIndex: 200, background: '#1A1A1A', overflow: 'hidden',
        animation: `${closing ? 'wrFadeOut' : 'wrFadeIn'} 0.3s ease-out both`,
        fontFamily: 'Nunito Sans, sans-serif',
      }}
    >
      <style>{WRAPPED_CSS}</style>

      {/* music — YouTube only mounts after the tap so its autoplay is allowed */}
      {ytId && started ? (
        <iframe
          ref={ytRef}
          title="wedding-song"
          src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=0&controls=0&loop=1&playlist=${ytId}&enablejsapi=1`}
          allow="autoplay"
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none', border: 0 }}
        />
      ) : !ytId ? (
        <audio ref={audioRef} src={fallbackAudioUrl} loop preload="auto" />
      ) : null}

      {/* ▶ Tap to begin — guarantees music + animations start on a gesture */}
      {!started && (
        <>
          <Stage petals background="radial-gradient(circle at 50% 45%, #2C1810 0%, #0D0705 75%)">
            <LogoWhite height={26} />
            <CouplePortraits w={w} size={76} delay={0.1} />
            <div style={{ fontFamily: 'Gibs, serif', fontSize: 'clamp(26px, 8vw, 36px)', color: '#fff', marginTop: 18, lineHeight: 1.3 }}>
              {w.couple_names || 'A love story'}
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 12, letterSpacing: 2, textTransform: 'uppercase' }}>
              Your Wedding Wrapped
            </div>
            <button
              onClick={begin}
              style={{
                marginTop: 38, display: 'inline-flex', alignItems: 'center', gap: 10,
                background: TERRACOTTA, color: '#fff', border: 'none', borderRadius: 100,
                padding: '15px 34px', cursor: 'pointer',
                fontFamily: 'Nunito Sans, sans-serif', fontSize: 15, fontWeight: 700,
                boxShadow: '0 8px 28px rgba(196,130,106,0.45)',
              }}
            >
              <span style={{ fontSize: 12 }}>{'\u25B6'}</span> Tap to begin
            </button>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 16 }}>
              Sound on for the full experience
            </div>
          </Stage>
          <button onClick={handleClose} aria-label="Close" style={cornerBtn('right')}>
            <X size={22} color="#fff" />
          </button>
        </>
      )}

      {started && (
        <>
          {/* progress bar — Instagram Stories style */}
          <div style={{ position: 'absolute', top: 12, left: 12, right: 12, display: 'flex', gap: 4, zIndex: 20 }}>
            {slides.map((s, i) => (
              <div key={s} style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.25)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 2, background: '#fff',
                  width: i < index ? '100%' : i === index ? '100%' : '0%',
                  opacity: i === index ? 1 : i < index ? 0.9 : 0,
                  transition: 'opacity 0.3s',
                }} />
              </div>
            ))}
          </div>

          {/* mute + close */}
          <button onClick={toggleMute} aria-label={muted ? 'Unmute music' : 'Mute music'} style={cornerBtn('left')}>
            {muted ? <VolumeX size={20} color="#fff" /> : <Volume2 size={20} color="#fff" />}
          </button>
          <button onClick={handleClose} aria-label="Close" style={cornerBtn('right')}>
            <X size={22} color="#fff" />
          </button>

          {/* nav arrows */}
          {index > 0 && (
            <button onClick={() => go(-1)} aria-label="Previous" style={navBtn('left')}>
              <ChevronLeft size={26} color="#fff" />
            </button>
          )}
          {index < slides.length - 1 && (
            <button onClick={() => go(1)} aria-label="Next" style={navBtn('right')}>
              <ChevronRight size={26} color="#fff" />
            </button>
          )}

          {/* the slide */}
          <div
            key={index}
            style={{
              position: 'absolute', inset: 0,
              animation: `${dirRef.current > 0 ? 'wrSlideInRight' : 'wrSlideInLeft'} 0.4s ease-out both`,
            }}
          >
            {current === 'opening' && <SlideOpening w={w} />}
            {current === 'guests' && <SlideGuests guests={guests} theme={theme} />}
            {current === 'journey' && <SlideJourney w={w} tasks={tasks} theme={theme} />}
            {current === 'photos' && <SlidePhotos photos={photos} />}
            {current === 'gallery' && <SlideGallery urls={galleryUrls} />}
            {current === 'messages' && <SlideMessages messages={messages} theme={theme} />}
            {current === 'gifts' && <SlideGifts registry={registry} gifts={gifts} money={money} theme={theme} />}
            {current === 'asoebi' && <SlideAsoebi asoebi={asoebi} money={money} publicMode={publicMode} theme={theme} />}
            {current === 'closing' && <SlideClosing w={w} guests={guests} photos={photos} publicMode={publicMode} theme={theme} />}
          </div>
        </>
      )}
    </div>
  );
}

/* ════════════════ shared pieces ════════════════ */

function Stage({ background, petals, backdrop, children }) {
  return (
    <div style={{ position: 'absolute', inset: 0, background }}>
      {/* FallingPetals renders position:absolute inset:0 at zIndex:0 —
          content sits in a zIndex:1 layer so it always paints above */}
      {petals && <FallingPetals density={8} />}
      {backdrop}
      <div style={{
        position: 'relative', zIndex: 1, height: '100%',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', textAlign: 'center', padding: '0 28px',
      }}>
        {children}
      </div>
    </div>
  );
}

function Reveal({ delay = 0, children, style }) {
  return (
    <div className="wr-fade-up" style={{ animationDelay: `${delay}s`, ...style }}>
      {children}
    </div>
  );
}

// Okoiya logo, forced white for dark slides; falls back to the wordmark
// if /okoiya-logo.png can't load.
function LogoWhite({ height = 28 }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <div style={{ fontFamily: 'Gibs, serif', fontSize: height, color: '#fff' }}>Okoiya</div>;
  }
  return (
    <img
      src="/okoiya-logo.png"
      alt="Okoiya"
      onError={() => setFailed(true)}
      style={{ height, objectFit: 'contain', display: 'block', filter: 'brightness(0) invert(1)' }}
    />
  );
}

// The couple's own faces, overlapping circles with a terracotta ring.
function CouplePortraits({ w, size = 84, delay = 0.15 }) {
  const p1 = w.partner1_photo_url, p2 = w.partner2_photo_url;
  if (!p1 && !p2) return null;
  const imgStyle = (z, overlap) => ({
    width: size, height: size, borderRadius: '50%', objectFit: 'cover',
    border: `3px solid ${TERRACOTTA}`, position: 'relative', zIndex: z,
    marginLeft: overlap ? -size * 0.22 : 0,
    boxShadow: '0 6px 22px rgba(0,0,0,0.45)',
  });
  return (
    <Reveal delay={delay}>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
        {p1 && <img src={p1} alt="" style={imgStyle(2, false)} />}
        {p2 && <img src={p2} alt="" style={imgStyle(1, !!p1)} />}
      </div>
    </Reveal>
  );
}

/* ════════════════ slides ════════════════ */

// 1 — THE OPENING
function SlideOpening({ w }) {
  const [p1, p2] = splitNames(w);
  return (
    <Stage petals background="radial-gradient(circle at 50% 45%, #2C1810 0%, #0D0705 75%)">
      <Reveal delay={0}>
        <LogoWhite height={28} />
      </Reveal>
      <CouplePortraits w={w} delay={0.25} />
      <Reveal delay={0.5}>
        <div style={{ fontFamily: 'Gibs, serif', fontSize: 'clamp(30px, 9vw, 42px)', color: '#fff', marginTop: 22, lineHeight: 1.25 }}>
          {p1} <span style={{ color: TERRACOTTA }}>&amp;</span> {p2}
        </div>
      </Reveal>
      <Reveal delay={0.8}>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', marginTop: 18, letterSpacing: 2, textTransform: 'uppercase' }}>
          Your Wedding Story
        </div>
      </Reveal>
      <Reveal delay={1.1}>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 10 }}>
          {fmtDate(w.wedding_date)}
        </div>
      </Reveal>
    </Stage>
  );
}

// 2 — THE GUESTS
function SlideGuests({ guests, theme }) {
  const big = guests.arrived > 0 ? guests.arrived : guests.confirmed || guests.total;
  const n = useCountUp(big, 1500, true);
  return (
    <Stage background={theme.guests}>
      <div style={{ fontFamily: 'Gibs, serif', fontSize: 'clamp(72px, 22vw, 96px)', color: '#fff', lineHeight: 1 }}>
        {n.toLocaleString()}
      </div>
      <Reveal delay={0.4}>
        <div style={{ fontSize: 18, color: 'rgba(255,255,255,0.88)', marginTop: 16 }}>
          {guests.arrived > 0 ? 'people came to celebrate your love' : 'people said yes to your love'}
        </div>
      </Reveal>
      {guests.total > 0 && (
        <Reveal delay={0.8}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 12 }}>
            of {guests.total} invited
            {guests.attendance_rate != null ? ` \u00B7 ${guests.attendance_rate}% attendance` : ''}
          </div>
        </Reveal>
      )}
    </Stage>
  );
}

// 3 — THE PLANNING JOURNEY
function SlideJourney({ w, tasks, theme }) {
  const days = useCountUp(w.days_planning || 0, 1200, true);
  return (
    <Stage background={theme.journey}>
      {(w.days_planning || 0) > 0 && (
        <Reveal delay={0}>
          <div style={{ fontFamily: 'Gibs, serif', fontSize: 'clamp(54px, 16vw, 72px)', color: '#fff', lineHeight: 1 }}>{days}</div>
          <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.85)', marginTop: 8 }}>days of planning</div>
        </Reveal>
      )}
      {(tasks.completed || 0) > 0 && (
        <Reveal delay={1.0} style={{ marginTop: 40 }}>
          <div style={{ fontFamily: 'Gibs, serif', fontSize: 'clamp(54px, 16vw, 72px)', color: '#fff', lineHeight: 1 }}>{tasks.completed}</div>
          <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.85)', marginTop: 8 }}>tasks completed</div>
        </Reveal>
      )}
      <Reveal delay={1.8}>
        <div style={{ fontSize: 16, fontStyle: 'italic', color: '#fff', marginTop: 48 }}>
          From your first yes to your forever
        </div>
      </Reveal>
    </Stage>
  );
}

// 4 — THE PHOTOS (the big number over the blurred mosaic)
function SlidePhotos({ photos }) {
  const urls = Array.isArray(photos.guest_urls) ? photos.guest_urls.slice(0, 12) : [];
  const guestCount = photos.guest_total || 0;
  const big = guestCount > 0 ? guestCount : photos.studio_total || 0;
  const n = useCountUp(big, 1500, true);
  const mosaic = urls.length > 0 ? (
    <div style={{
      position: 'absolute', inset: 0, display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: '1fr',
      filter: 'blur(8px)', opacity: 0.3, pointerEvents: 'none',
    }}>
      {urls.map((u, i) => (
        <img
          key={i} src={u} alt=""
          className="wr-tile"
          style={{ width: '100%', height: '100%', objectFit: 'cover', animationDelay: `${i * 0.05}s` }}
        />
      ))}
    </div>
  ) : null;

  return (
    <Stage background="#111111" backdrop={mosaic}>
      <div style={{ fontFamily: 'Gibs, serif', fontSize: 'clamp(72px, 22vw, 96px)', color: '#fff', lineHeight: 1 }}>
        {n.toLocaleString()}
      </div>
      <Reveal delay={0.5}>
        <div style={{ fontSize: 18, color: 'rgba(255,255,255,0.9)', marginTop: 14 }}>
          {guestCount > 0 ? 'moments your guests captured' : 'studio memories you preserved'}
        </div>
      </Reveal>
    </Stage>
  );
}

// 5 — THE GALLERY (clear polaroids of their real photos)
function SlideGallery({ urls }) {
  const tilts = [-6, 4, -3, 5, -4];
  return (
    <Stage background="radial-gradient(circle at 50% 40%, #221409 0%, #100904 80%)">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center', maxWidth: 440 }}>
        {urls.map((u, i) => (
          <div key={i} style={{ transform: `rotate(${tilts[i % tilts.length]}deg)` }}>
            <div
              className="wr-tile"
              style={{
                animationDelay: `${0.15 + i * 0.18}s`,
                background: '#fff', padding: '7px 7px 22px',
                boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              }}
            >
              <img src={u} alt="" style={{ width: 112, height: 112, objectFit: 'cover', display: 'block' }} />
            </div>
          </div>
        ))}
      </div>
      <Reveal delay={0.3 + urls.length * 0.18}>
        <div style={{ fontFamily: 'Gibs, serif', fontStyle: 'italic', fontSize: 19, color: '#fff', marginTop: 34 }}>
          the moments you'll keep forever
        </div>
      </Reveal>
    </Stage>
  );
}

// 6 — WORDS OF LOVE (guest gift messages, word by word) — private only
function SlideMessages({ messages, theme }) {
  const m = messages[0];
  const words = String(m.text || '').split(/\s+/).filter(Boolean);
  const doneAt = 0.4 + words.length * 0.08;
  return (
    <Stage background="radial-gradient(circle at 50% 38%, #1A0F0A 0%, #090403 80%)">
      <div style={{
        maxWidth: 400, fontFamily: 'Gibs, serif', fontStyle: 'italic',
        fontSize: 'clamp(17px, 5vw, 21px)', lineHeight: 1.8, color: '#fff',
      }}>
        {'\u201C'}
        {words.map((wd, i) => (
          <span key={i} className="wr-word" style={{ animationDelay: `${0.4 + i * 0.08}s` }}>{wd}{' '}</span>
        ))}
        {'\u201D'}
      </div>
      {m.name && (
        <Reveal delay={doneAt + 0.4}>
          <div style={{ fontSize: 14, color: theme.accent, marginTop: 22 }}>{'\u2014'} {m.name}</div>
        </Reveal>
      )}
      <Reveal delay={doneAt + 0.9}>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginTop: 18 }}>
          {messages.length > 1
            ? `and ${messages.length - 1} more ${messages.length - 1 === 1 ? 'note' : 'notes'} of love came with your gifts`
            : 'left with a gift, kept forever'}
        </div>
      </Reveal>
    </Stage>
  );
}

// 7 — THE GIFTS (skipped in publicMode and when no gift data)
function SlideGifts({ registry, gifts, money, theme }) {
  return (
    <Stage background={theme.gifts}>
      <svg width="110" height="110" viewBox="0 0 100 100" style={{ overflow: 'visible' }} aria-hidden="true">
        <rect x="18" y="42" width="64" height="46" rx="4" fill="#fff" opacity="0.92" />
        <rect x="44" y="42" width="12" height="46" fill="#C4826A" />
        <g className="wr-lid">
          <rect x="12" y="28" width="76" height="16" rx="4" fill="#fff" />
          <rect x="44" y="28" width="12" height="16" fill="#C4826A" />
          <path d="M50 28 C40 12, 24 14, 30 26 C34 32, 44 30, 50 28 Z" fill="#C4826A" />
          <path d="M50 28 C60 12, 76 14, 70 26 C66 32, 56 30, 50 28 Z" fill="#C4826A" />
        </g>
      </svg>
      {(registry.received || 0) > 0 && (
        <Reveal delay={0.9}>
          <div style={{ fontFamily: 'Gibs, serif', fontSize: 'clamp(40px, 12vw, 56px)', color: '#fff', marginTop: 24, lineHeight: 1 }}>
            {registry.received}
          </div>
          <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.9)', marginTop: 6 }}>wishes gifted</div>
        </Reveal>
      )}
      {(gifts.cash_total || 0) > 0 && (
        <Reveal delay={1.4}>
          <div style={{ fontFamily: 'Gibs, serif', fontSize: 'clamp(34px, 10vw, 48px)', color: '#fff', marginTop: 28, lineHeight: 1 }}>
            {money(gifts.cash_total)}
          </div>
          <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.9)', marginTop: 6 }}>in love received</div>
        </Reveal>
      )}
    </Stage>
  );
}

// 8 — THE ASO EBI
function SlideAsoebi({ asoebi, money, publicMode, theme }) {
  return (
    <Stage background={theme.asoebi}>
      <svg width="120" height="120" viewBox="0 0 100 100" className="wr-unfold" aria-hidden="true">
        <path d="M14 22 Q30 12, 50 20 T 86 22 L86 78 Q70 88, 50 80 T 14 78 Z"
          fill="#C4826A" opacity="0.95" />
        <path d="M14 36 Q30 28, 50 34 T 86 36" stroke="#FAFAF8" strokeWidth="1.6" fill="none" opacity="0.5" />
        <path d="M14 50 Q30 42, 50 48 T 86 50" stroke="#FAFAF8" strokeWidth="1.6" fill="none" opacity="0.5" />
        <path d="M14 64 Q30 56, 50 62 T 86 64" stroke="#FAFAF8" strokeWidth="1.6" fill="none" opacity="0.5" />
      </svg>
      <Reveal delay={0.6}>
        <div style={{ fontFamily: 'Gibs, serif', fontSize: 'clamp(48px, 14vw, 64px)', color: '#fff', marginTop: 22, lineHeight: 1 }}>
          {asoebi.orders}
        </div>
        <div style={{ fontSize: 17, color: 'rgba(255,255,255,0.9)', marginTop: 8 }}>people wore your colours</div>
      </Reveal>
      {!publicMode && (asoebi.revenue || 0) > 0 && (
        <Reveal delay={1.1}>
          <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.7)', marginTop: 22 }}>
            {money(asoebi.revenue)} in Aso Ebi love
          </div>
        </Reveal>
      )}
    </Stage>
  );
}

// 9 — THE CLOSING + SHARE
function SlideClosing({ w, guests, photos, publicMode, theme }) {
  const [copied, setCopied] = useState(false);
  const [p1, p2] = splitNames(w);
  const wrappedUrl = `${APP_URL}/wrapped/${w.slug || ''}`;

  const shareWhatsApp = () => {
    const text = `We are so glad to be an Okoiya Couple! ${p1} & ${p2} - Our Wedding Wrapped \u2728 ${wrappedUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(wrappedUrl);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = wrappedUrl; document.body.appendChild(ta);
      ta.select(); document.execCommand('copy'); ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCard = () => drawShareCard({ w, guests, photos });

  const btn = {
    background: 'rgba(255,255,255,0.12)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.4)', borderRadius: 10,
    padding: '11px 16px', cursor: 'pointer',
    fontFamily: 'Nunito Sans, sans-serif', fontSize: 13, fontWeight: 700,
  };

  return (
    <Stage petals background="radial-gradient(circle at 50% 45%, #2C1810 0%, #0D0705 75%)">
      <svg width="96" height="96" viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="50" r="42" fill="none" stroke={TERRACOTTA} strokeWidth="5"
          className="wr-ring" strokeLinecap="round" transform="rotate(-90 50 50)" />
      </svg>
      <Reveal delay={0.6}>
        <div style={{ fontFamily: 'Gibs, serif', fontSize: 'clamp(28px, 8vw, 36px)', color: '#fff', marginTop: 22 }}>
          {p1} &amp; {p2}
        </div>
      </Reveal>
      <Reveal delay={1.0}>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', marginTop: 10 }}>{fmtDate(w.wedding_date)}</div>
      </Reveal>
      {w.hashtag && (
        <Reveal delay={1.3}>
          <div style={{ fontSize: 13, color: theme.accent, marginTop: 8 }}>{w.hashtag}</div>
        </Reveal>
      )}
      <div className="wr-divider" style={{ marginTop: 20 }} />
      <Reveal delay={1.9}>
        <div style={{ fontFamily: 'Gibs, serif', fontStyle: 'italic', fontSize: 22, color: TERRACOTTA, marginTop: 22 }}>
          Forever starts now.
        </div>
      </Reveal>
      {(w.days_married || 0) > 0 && (
        <Reveal delay={2.4}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 12 }}>
            Married {w.days_married} {w.days_married === 1 ? 'day' : 'days'} {'\u2014'} and counting.
          </div>
        </Reveal>
      )}

      <Reveal delay={3.1} style={{ marginTop: 38 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14 }}>
          Share your story
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button style={{ ...btn, background: '#25D366', border: 'none' }} onClick={shareWhatsApp}>
            Share to WhatsApp
          </button>
          <button style={btn} onClick={copyLink}>{copied ? 'Copied!' : 'Copy Link'}</button>
          {!publicMode && <button style={btn} onClick={downloadCard}>Download</button>}
        </div>
      </Reveal>
    </Stage>
  );
}

/* ════════════════ share card (canvas PNG, 1080×1920) ════════════ */

async function drawShareCard({ w, guests, photos }) {
  const [p1, p2] = splitNames(w);
  const c = document.createElement('canvas');
  c.width = 1080; c.height = 1920;
  const ctx = c.getContext('2d');

  try { await document.fonts.load('80px Gibs'); await document.fonts.load('700 36px "Nunito Sans"'); } catch { /* fall back */ }

  const grad = ctx.createRadialGradient(540, 860, 100, 540, 960, 1300);
  grad.addColorStop(0, '#2C1810'); grad.addColorStop(1, '#0D0705');
  ctx.fillStyle = grad; ctx.fillRect(0, 0, 1080, 1920);

  ctx.strokeStyle = TERRACOTTA; ctx.lineWidth = 14;
  ctx.beginPath(); ctx.arc(540, 560, 170, 0, Math.PI * 2); ctx.stroke();

  // couple portraits inside the ring, clipped to circles
  const loadImg = (src) => new Promise((res) => {
    if (!src) return res(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => res(img);
    img.onerror = () => res(null);
    img.src = src;
  });
  const [i1, i2] = await Promise.all([loadImg(w.partner1_photo_url), loadImg(w.partner2_photo_url)]);
  const drawRound = (img, cx, cy, r) => {
    if (!img) return;
    ctx.save();
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip();
    const s = Math.max((r * 2) / img.width, (r * 2) / img.height);
    ctx.drawImage(img, cx - (img.width * s) / 2, cy - (img.height * s) / 2, img.width * s, img.height * s);
    ctx.restore();
    ctx.strokeStyle = TERRACOTTA; ctx.lineWidth = 8;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
  };
  if (i1 && i2) { drawRound(i1, 470, 560, 95); drawRound(i2, 614, 560, 95); }
  else drawRound(i1 || i2, 540, 560, 110);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#FAFAF8';
  ctx.font = '64px Gibs, serif';
  ctx.fillText('Okoiya', 540, 220);

  ctx.font = '88px Gibs, serif';
  ctx.fillText(p1, 540, 880);
  ctx.fillStyle = TERRACOTTA; ctx.fillText('&', 540, 985);
  ctx.fillStyle = '#FAFAF8'; ctx.fillText(p2, 540, 1090);

  ctx.font = '36px "Nunito Sans", sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.fillText(fmtDate(w.wedding_date), 540, 1180);

  const stats = [];
  const big = guests.arrived > 0 ? guests.arrived : guests.total;
  if (big > 0) stats.push(`${big} guests`);
  const ph = (photos.guest_total || 0) + (photos.studio_total || 0);
  if (ph > 0) stats.push(`${ph} memories`);
  if ((w.days_planning || 0) > 0) stats.push(`${w.days_planning} days of planning`);
  if (stats.length) {
    ctx.font = '700 40px "Nunito Sans", sans-serif';
    ctx.fillStyle = '#FAFAF8';
    ctx.fillText(stats.join('  \u00B7  '), 540, 1330);
  }

  if (w.hashtag) {
    ctx.font = 'italic 38px Gibs, serif';
    ctx.fillStyle = TERRACOTTA;
    ctx.fillText(w.hashtag, 540, 1430);
  }

  ctx.font = '32px "Nunito Sans", sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.fillText('Our Wedding Wrapped \u2728', 540, 1700);
  ctx.fillStyle = TERRACOTTA;
  ctx.fillText('okoiya.com', 540, 1760);

  c.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'okoiya-wrapped.png';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }, 'image/png');
}

/* ════════════════ helpers + styles ════════════════ */

function splitNames(w) {
  if (w.partner1_name || w.partner2_name) return [w.partner1_name || '', w.partner2_name || ''];
  const parts = String(w.couple_names || '').split('&').map((s) => s.trim());
  return [parts[0] || 'You', parts[1] || 'Yours'];
}

function fmtDate(v) {
  if (!v) return '';
  const dt = new Date(v);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function extractYouTubeId(url) {
  const m = String(url).match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : null;
}

function cornerBtn(side) {
  return {
    position: 'absolute', top: 28, [side]: 16, zIndex: 30,
    background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%',
    width: 42, height: 42, display: 'flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer',
  };
}

function navBtn(side) {
  return {
    position: 'absolute', top: '50%', [side]: 10, transform: 'translateY(-50%)',
    zIndex: 30, background: 'rgba(255,255,255,0.10)', border: 'none',
    borderRadius: '50%', width: 44, height: 44, display: 'flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  };
}

const WRAPPED_CSS = `
@keyframes wrFadeIn  { from { opacity: 0 } to { opacity: 1 } }
@keyframes wrFadeOut { from { opacity: 1 } to { opacity: 0 } }
@keyframes wrSlideInRight { from { opacity: 0; transform: translateX(60px) } to { opacity: 1; transform: translateX(0) } }
@keyframes wrSlideInLeft  { from { opacity: 0; transform: translateX(-60px) } to { opacity: 1; transform: translateX(0) } }
@keyframes wrUp   { from { opacity: 0; transform: translateY(18px) } to { opacity: 1; transform: translateY(0) } }
@keyframes wrTile { from { opacity: 0; transform: scale(0.85) } to { opacity: 1; transform: scale(1) } }
@keyframes wrRing { to { stroke-dashoffset: 0 } }
@keyframes wrLine { from { transform: scaleX(0) } to { transform: scaleX(1) } }
@keyframes wrLid  {
  0%   { transform: translateY(0) rotate(0deg) }
  60%  { transform: translateY(-16px) rotate(-12deg) }
  100% { transform: translateY(-22px) rotate(-16deg) }
}
@keyframes wrFold {
  from { opacity: 0; transform: scale(0.8) rotate(-6deg) }
  to   { opacity: 1; transform: scale(1) rotate(0deg) }
}
.wr-fade-up { opacity: 0; animation: wrUp 0.6s ease-out forwards; }
.wr-tile    { opacity: 0; animation: wrTile 0.5s ease-out forwards; }
.wr-word    { opacity: 0; animation: wrUp 0.5s ease-out forwards; display: inline-block; }
.wr-unfold  { animation: wrFold 0.9s ease-out both; transform-origin: center; }
.wr-lid     { animation: wrLid 1s ease-out 0.4s both; transform-origin: 50px 36px; }
.wr-ring    {
  stroke-dasharray: 264; stroke-dashoffset: 264;
  animation: wrRing 1.5s ease-out 0.2s forwards;
}
.wr-divider {
  width: 100px; height: 1px; background: ${'#C4826A'};
  transform-origin: left center; animation: wrLine 0.8s ease-out 1.5s both;
}
@media (prefers-reduced-motion: reduce) {
  .wr-fade-up, .wr-tile, .wr-word, .wr-unfold, .wr-lid, .wr-divider { animation-duration: 0.01s; animation-delay: 0s; }
  .wr-ring { animation-duration: 0.01s; animation-delay: 0s; }
}
`;
